import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogHeader,
} from "../../../../../components/ui/dialog";
import { ArrowLeft, Server, Clock, Activity, Search, Settings, Link2, Box, Check, Filter, X, XCircle, CheckCircle, RefreshCw, AlertCircle, HelpCircle, Cog } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';

import { fetchDevices } from '../../../../../redux/device/actions';
import { updateDeviceStatus } from '../../../../../redux/device/saga';
import { formatDate } from '../../../../../utils/unitUtils';
import { Device, DeviceStatus, WeldTypeNames } from '../../../../device/types/device';
import { StatusUpdateDialog } from '../../../../device/components/DeviceManagement/StatusUpdateDialog';
import EdgeDeviceManagement from '../../../../device/components/DeviceManagement/EdgeDeviceManagement_restructured';
import { groupDevicesByStation, processDevicesLocation } from '../../../../../utils/deviceUtils';
import type { DashboardProject } from '../../../types/project';
import StationWall from './StationWall';

// Status badge colors
const statusBadgeColors: Record<any, string> = {
  Pending: 'text-readable-red bg-[rgba(196,0,31,0.06)]',
  Failed: 'text-readable-red bg-[rgba(196,0,31,0.06)]',
  CertificateAvailable: 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
  DownloadComplete: 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]',
  Deregistered: 'bg-gray-50 text-gray-700 border-gray-200',
  AdminApprovalPending: 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
  AdminApproved: "text-galvanizing-green bg-[rgba(31,154,58,0.08)]",
  AdminRejected: 'bg-gray-50 text-gray-700 border-gray-200',
  Register: 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
  PublishReady: 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]',
  AdminApproval: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]',
};

// User status map
const userStatusMap: Record<
  string,
  { label: string; color: string; backends: string[]; icon: any; description: string }
> = {
  'Approved': {
    label: "Registered",
    color: "text-emerald-400",
    backends: ["AdminApproved", "DownloadComplete"],
    icon: Server,
    description: "Device has been registered and ready to assign to project.",
  },
  'DeviceActive': {
    label: "Active",
    color: "text-green-700",
    backends: ["PublishReady"],
    icon: CheckCircle,
    description: "Devices have been assigned to project and are fully active.",
  },
  'Pending': {
    label: "Registration Pending",
    color: "text-amber-600",
    backends: ["Pending", "AdminApprovalPending", 'Register', "CertificateAvailable", 'Failed'],
    icon: Clock,
    description: "Devices are awaiting registration.",
  },
  'AdminApproval': {
    label: "Admin Approval",
    color: "text-blue-600",
    backends: ["AdminApproval"],
    icon: RefreshCw,
    description: "Devices are awaiting admin approval.",
  },
  'Decommissioned': {
    label: "Decommissioned",
    color: "text-red-600",
    backends: ["Deregistered", "AdminRejected"],
    icon: XCircle,
    description: "Devices are de-activated",
  },
};

const statusName: Record<any, string> = {
  Pending: 'Registration Pending',
  Failed: 'Registration Failed',
  CertificateAvailable: 'Registration Pending',
  DownloadComplete: 'Registered',
  Deregistered: 'Decommissioned',
  AdminApprovalPending: 'Awaiting Admin Approval',
  AdminApproved: "Approved",
  AdminRejected: 'Decommissioned',
  Register: 'Enable Re-registration',
  PublishReady: 'Active',
  AdminApproval: 'Admin Approval',
};

interface DeviceManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  assemblyNumber?: string;
  project: DashboardProject;
  onOpenWeldParameters?: () => void;
}

export const DeviceManagementModal: React.FC<DeviceManagementModalProps> = ({
  isOpen,
  onClose,
  assemblyNumber,
  project,
  onOpenWeldParameters,
}) => {
  const dispatch = useDispatch();
  const deviceState = useSelector((state: any) => state.deviceData);
  const { devices, devicesLoading, devicesError } = deviceState;

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<DeviceStatus | 'all'>('all');
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [isDeviceManagementOpen, setIsDeviceManagementOpen] = useState(false);
  const [selectedStatusCard, setSelectedStatusCard] = useState<string | null>(null);
  const [isStatusUpdateModalOpen, setIsStatusUpdateModalOpen] = useState(false);
  const [selectedStatusForUpdate, setSelectedStatusForUpdate] = useState<DeviceStatus | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Load devices on component mount
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchDevices({}));
    }
  }, [dispatch, isOpen]);

  // Ensure devices is an array and filter by project
  const devicesArray = Array.isArray(devices) ? devices : [];
  const projectDevices = devicesArray.filter((device: Device) =>
    device.project?.name === project.project.projectName ||
    device.project?.projectNumber === project.project.projectNumber
  );

  // Process devices to parse location data
  const processedDevices = processDevicesLocation(projectDevices);
 // console.log('processedDevices', processedDevices);

  // Group devices by station
  const devicesByStation = groupDevicesByStation(processedDevices);

  //console.log('processedDevices', processedDevices);

  // Extract unique statuses from processed devices
  const availableStatuses = Array.from(
    new Set(processedDevices.map((d: Device) => d.status))
  ).sort();

  const filteredDevices = processedDevices.filter((device: Device) => {
    const matchesSearch = device.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || device.status === statusFilter;

    // Apply status card filter
    let matchesStatusCard = true;
    if (selectedStatusCard) {
      const statusConfig = userStatusMap[selectedStatusCard];
      if (statusConfig) {
        matchesStatusCard = statusConfig.backends.includes(device.status as DeviceStatus);
      }
    }

    return matchesSearch && matchesStatus && matchesStatusCard;
  });

  // Group filtered devices by station
  const filteredDevicesByStation = groupDevicesByStation(filteredDevices);



  // Handle status card click
  const handleStatusCardClick = (cardKey: string) => {
    if (cardKey === 'total') {
      setSelectedStatusCard(null);
    } else if (selectedStatusCard === cardKey) {
      setSelectedStatusCard(null);
    } else {
      setSelectedStatusCard(cardKey);
    }
  };

  const handleDeviceSelect = (deviceId: string) => {
    setSelectedDevices(prev =>
      prev.includes(deviceId)
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDevices.length === filteredDevices.length) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(filteredDevices.map((d: Device) => d.id));
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedDevice || !selectedStatusForUpdate) return;

    setIsUpdatingStatus(true);
    try {
      const payload = {
        deviceId: selectedDevice.id,
        status: selectedStatusForUpdate,
      };

      await updateDeviceStatus(payload);
      await dispatch(fetchDevices({}));
      setIsStatusUpdateModalOpen(false);
      setSelectedStatusForUpdate(null);
    } catch (error) {
      console.error('Failed to update device status', error);
    } finally {
      setIsUpdatingStatus(false);
      setIsDeviceManagementOpen(false);
    }
  };

  const getStatusBadge = (status: DeviceStatus) => (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${statusBadgeColors[status]}`}>
      {statusName[status] || status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );




  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[100vw] w-screen h-screen max-h-screen p-0 gap-0 overflow-y-auto">
        <div className="flex flex-col h-full">

          {!isDeviceManagementOpen && (
            <div>
              <DialogHeader className="flex-shrink-0 px-6 py-4 pr-14 border-b border-[#E2E5EA] bg-white flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button
                    onClick={onClose}
                    variant="outline"
                    size="sm"
                    className="shrink-0 h-9 w-9 p-0 border-[#E2E5EA] hover:bg-page-bg"
                  >
                    <ArrowLeft className="h-4 w-4 text-text-grey" />
                  </Button>
                  <div>
                    <h2 className="text-[18px] font-semibold text-blueprint-blue leading-tight">Device Overview</h2>
                    <p className="text-[13px] text-heritage-grey mt-0.5">
                      {project.project.projectName} - {project.project.projectNumber}
                    </p>
                  </div>
                </div>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto bg-page-bg">
                <div className="h-full overflow-auto p-6">
                  {devicesLoading ? (

                    <div className="flex items-center justify-center min-h-[400px]">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
                      <span className="text-gray-600 text-lg ml-3">Loading devices...</span>
                    </div>
                  ) :
                   <>{processedDevices.length > 0 && !devicesLoading && <StationWall devices={processedDevices} onOpenWeldParameters={onOpenWeldParameters} />}</>

                  }


                  {/* Device List by Station */}
                  <div className="space-y-6">


                    {/* No devices message */}
                    {Object.keys(filteredDevicesByStation).length === 0 && !devicesLoading && (
                      <div className="text-center py-12">
                        <Server className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No devices found</h3>
                        <p className="text-gray-500">

                          No devices are currently assigned to this project.'

                        </p>

                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}


          {/* Status Update Dialog */}
          <StatusUpdateDialog
            isOpen={isStatusUpdateModalOpen}
            onClose={() => {
              setIsStatusUpdateModalOpen(false);
              setSelectedStatusForUpdate(null);
            }}
            selectedDevice={selectedDevice}
            selectedStatusForUpdate={selectedStatusForUpdate}
            setSelectedStatusForUpdate={setSelectedStatusForUpdate}
            onStatusUpdate={handleStatusUpdate}
            isUpdatingStatus={isUpdatingStatus}
            availableStatuses={availableStatuses}
          />

          {/* Edge Device Management */}
          {selectedDevice && isDeviceManagementOpen && (
            <EdgeDeviceManagement
              device={selectedDevice}
              onStatusChange={(status: DeviceStatus) => {
                // TODO: Implement Redux action for status change
              }}
              onReboot={() => {
                // TODO: Implement Redux action for device reboot
              }}
              onFirmwareUpdate={() => {
                // TODO: Implement Redux action for firmware update
              }}
              onClose={() => {
                setSelectedDevice(null);
                setIsDeviceManagementOpen(false);
              }}
              onSettingsClick={() => {
                setSelectedDevice(selectedDevice);
                setIsStatusUpdateModalOpen(true);
              }}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};